// The results of a scan, told for a person who is not an expert: what was analysed, the team's work
// and the manual review (the analysis figures stay on the Análisis tab), where to begin, the score as
// secondary information, and the findings in categories that are never mixed. The same tabs also tell
// one page. Figures come from results-logic.ts, so a number in a summary has the same source as the
// items it counts.
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ManualReview,
  type PageIssue,
  type PassedCheck,
  type Priority,
  REVIEW_ANSWERS,
  type ScanDetail,
  type ScanIssue,
  type ScannedPage,
  scansQuery,
} from '../api';
import { elementsOf } from '../element-logic';
import { usePersisted } from '../persist';
import {
  applyFilters,
  type Filters,
  groupProblems,
  hasFilters,
  isDelegatedOnly,
  isFullyDiscarded,
  NO_FILTERS,
  type Problem,
  splitCategories,
  startHere,
  summarize,
  tabOf,
} from '../results-logic';
import { HelpPopover, WebsiteLink } from '../ui';
import { pathOf, publicPage } from '../util';
import { ToneIcon } from '../viz';
import { CheckOutcome } from './check-result';
import { CorrectionPanel } from './element-guide';
import type { Rights } from './follow-state';
import { FollowSummaryCard } from './follow-tab';
import { GuideLink, HelpBadge } from './manual-parts';
import { FilterBar, Note, type PageContext, PRIORITY_TONE, PriorityChip, ProblemCard } from './results-parts';
import { ScoreHero, useDate } from './scan-parts';

export type TabKey = 'problems' | 'passed' | 'unchecked' | 'manual' | 'other';

const anchorOf = (key: string) => `problema-${key.replace(/[^a-z0-9]+/gi, '-')}`;

/**
 * What every screen says about the limits of automated testing: one quiet sentence, placed after the
 * results (never before them), with the way to how the analysis works.
 */
export function Honesty() {
  const { t, i18n: i18next } = useTranslation();
  return (
    <p className="results-honesty">
      {t('results.honesty.text')}{' '}
      <WebsiteLink href={publicPage(i18next.language, 'methodology')}>
        {t('results.honesty.link')}
      </WebsiteLink>
    </p>
  );
}

type CountingTerm = 'problem' | 'element' | 'appearance' | 'devices' | 'pages' | 'pageCount' | 'atLeast';

/** What the figures count, term by term: where the explanations that do not belong beside the numbers live. */
export function CountingTerms({
  terms = ['problem', 'element', 'devices', 'pageCount'],
  repeated = null,
}: {
  terms?: CountingTerm[];
  /** Set when an element fails in several problems: the totals then differ by this much. */
  repeated?: { summed: number; total: number } | null;
}) {
  const { t } = useTranslation();
  return (
    <dl className="summary-how">
      {terms.map((term) => (
        <div key={term}>
          <dt>{t(`results.summary.how.${term}.term`)}</dt>
          <dd>{t(`results.summary.how.${term}.text`)}</dd>
        </div>
      ))}
      {repeated && (
        <div>
          <dt>{t('results.summary.how.repeated.term')}</dt>
          <dd>{t('results.summary.repeated', repeated)}</dd>
        </div>
      )}
    </dl>
  );
}

/** A number of elements, with a "+" when the engine stopped counting; screen readers hear "at least". */
function ElementsCount({ value, capped }: { value: number; capped: boolean }) {
  const { t } = useTranslation();
  return capped ? (
    <>
      <span aria-hidden="true">{value}+</span>
      <span className="sr-only">
        {t('results.summary.atLeast')} {value}
      </span>
    </>
  ) : (
    <>{value}</>
  );
}

/** Priorities in one quiet line («3 de prioridad alta · 2 de prioridad media»): only the ones there are. */
function priorityLine(t: TFunction, byPriority: Record<'alta' | 'media' | 'baja', number>): string | null {
  const present = (['alta', 'media', 'baja'] as const).filter((priority) => byPriority[priority] > 0);
  if (present.length === 0) return null;
  return present
    .map((priority) => t(`results.summary.priorities.${priority}`, { count: byPriority[priority] }))
    .join(' · ');
}

/**
 * How the figures of the summary are counted, one level down: closed, small and quiet, for whoever wants to
 * know. It sits in «Sobre este análisis» of the analysis tab, and under the summary where the summary is the
 * whole screen. The estimate’s methodology link lives here too (not beside the score).
 */
export function CountingDetails({ problems, pages }: { problems: Problem[]; pages: ScannedPage[] }) {
  const { t, i18n: i18next } = useTranslation();
  const summary = summarize(problems, pages);
  return (
    <details className="issue-details counting-details">
      <summary>{t('results.summary.how.title')}</summary>
      <CountingTerms
        terms={[
          'problem',
          'element',
          ...(summary.capped ? (['atLeast'] as const) : []),
          'appearance',
          'devices',
          'pages',
          'pageCount',
        ]}
        repeated={summary.repeated ? { summed: summary.elementsSummed, total: summary.elements } : null}
      />
      <p className="counting-method">
        <WebsiteLink href={publicPage(i18next.language, 'methodology')}>
          {t('results.score.method')}
        </WebsiteLink>
      </p>
    </details>
  );
}

/**
 * The summary of the analysis: three figure cards (same language as «Tus webs»), label above the number,
 * a short hint under it. How the figures are counted lives one level down (`explain` puts «How are these
 * figures counted?» under the summary; the analysis tab shows it in «Sobre este análisis» instead).
 */
export function AnalysisSummary({
  problems,
  pages,
  discarded = 0,
  headingLevel = 3,
  action,
  explain = true,
}: {
  problems: Problem[];
  pages: ScannedPage[];
  /** Elements the team discarded with a justification: they are not in the figures. */
  discarded?: number;
  headingLevel?: 2 | 3 | 4;
  /** The way to every problem, under the figures. */
  action?: ReactNode;
  /** Shows how the figures are counted under the summary; false when the screen has it elsewhere. */
  explain?: boolean;
}) {
  const { t } = useTranslation();
  const summary = summarize(problems, pages);
  const Heading = `h${headingLevel}` as const;
  // The same element in a header or a footer shared by several pages counts on each page (each keeps its own
  // record): how many of the elements are such repeats, so the total is not read as that many separate things.
  const repeatedAcross = problems
    .filter((problem) => problem.result === 'violation' && !isFullyDiscarded(problem))
    .reduce(
      (sum, problem) =>
        sum + problem.shared.reduce((inner, component) => inner + component.pageKeys.length - 1, 0),
      0,
    );
  const problemsHint = [
    priorityLine(t, summary.byPriority),
    summary.global > 0 ? t('results.summary.globalProblems', { count: summary.global }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <section className="summary-card" aria-labelledby="resumen-analisis">
      <Heading id="resumen-analisis" tabIndex={-1} className="focus:outline-none">
        {t('results.summary.title')}
      </Heading>
      {summary.problems === 0 ? (
        <p>{t('results.summary.none', { count: summary.pagesAnalysed })}</p>
      ) : (
        <dl className="summary-figure-cards">
          <div data-tone="critica">
            <dt>
              {t('results.summary.problems')}
              <HelpPopover label={t('results.summary.problemsHelp')}>
                <p>{t('results.summary.problemsHelpText')}</p>
                {summary.capped && <p>{t('results.summary.how.atLeast.text')}</p>}
              </HelpPopover>
            </dt>
            <dd>
              <strong>{summary.problems}</strong>
              {problemsHint ? <span className="figure-hint">{problemsHint}</span> : null}
            </dd>
          </div>
          <div>
            <dt>
              {t('results.summary.elements')}
              <HelpPopover label={t('results.summary.elementsHelp')}>
                <p>{t('results.summary.elementsHelpText')}</p>
              </HelpPopover>
            </dt>
            <dd>
              <strong>
                <ElementsCount value={summary.elements} capped={summary.capped} />
              </strong>
              {repeatedAcross > 0 && (
                <span className="figure-hint">
                  {t('results.summary.sharedElements', { count: repeatedAcross })}
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt>{t('results.summary.pages')}</dt>
            <dd>
              <strong>{summary.pagesAffected}</strong>
              <span className="figure-hint">
                {t('results.summary.pagesOfTotal', {
                  count: summary.pagesAffected,
                  total: summary.pagesAnalysed,
                })}
              </span>
            </dd>
          </div>
        </dl>
      )}
      {discarded > 0 && <p className="text-sm">{t('follow.summary.discarded', { count: discarded })}</p>}
      {explain && <CountingDetails problems={problems} pages={pages} />}
      {action && <div className="summary-action">{action}</div>}
    </section>
  );
}

/** Where the manual review stands: none of it is done by the automated test. */
function ManualSummary({
  manual,
  review,
  onOpen,
}: {
  manual: ManualReview | null;
  review: Problem[];
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  // Elements the team confirmed or discarded, among all those the analysis left to review.
  const decided = review.reduce((sum, problem) => sum + problem.follow.confirmed + problem.discarded, 0);
  const total = review.reduce((sum, problem) => sum + problem.elements + problem.discarded, 0);
  return (
    <section className="summary-card" aria-labelledby="resumen-manual">
      <h3 id="resumen-manual">{t('results.manual.title')}</h3>
      <dl className="summary-figures">
        <div>
          <dt>{t('results.manual.figures.registered')}</dt>
          <dd>{manual?.registered ?? 0}</dd>
        </div>
        <div>
          <dt>{t('results.manual.figures.pending')}</dt>
          <dd>{manual ? manual.total - manual.registered : 0}</dd>
        </div>
        <div>
          <dt>{t('results.manual.figures.decide')}</dt>
          <dd>{Math.max(0, total - decided)}</dd>
        </div>
      </dl>
      <p className="summary-priority-line">{t('results.manual.hint')}</p>
      <p className="summary-foot">
        <button type="button" className="link-button" onClick={onOpen}>
          {t('results.manual.open')}
        </button>
      </p>
    </section>
  );
}

/** Where to begin: the first problems of the common order, and how that order is decided. */
export function StartHere({ problems, onOpen }: { problems: Problem[]; onOpen: (problem: Problem) => void }) {
  const { t } = useTranslation();
  const first = startHere(problems);
  if (first.length === 0) return null;
  return (
    <section className="start-here" aria-labelledby="por-donde-empezar">
      <h3 id="por-donde-empezar">{t('results.start.title')}</h3>
      <p>{t('results.start.intro')}</p>
      <ol>
        {first.map((problem, index) => (
          <li key={problem.key}>
            <span className="start-number" aria-hidden="true">
              {index + 1}
            </span>
            <div>
              <p>
                <strong>{problem.title}</strong> <PriorityChip priority={problem.priority} />
              </p>
              <p className="text-sm text-ink-muted">
                {t(problem.capped ? 'results.start.countsAtLeast' : 'results.start.counts', {
                  count: problem.elements,
                  pages: t('results.pagesCount', { count: problem.pages }),
                })}
              </p>
            </div>
            <button type="button" className="start-link" onClick={() => onOpen(problem)}>
              {t('results.actions.fix')}
              <span className="sr-only"> — {problem.title}</span>
            </button>
          </li>
        ))}
      </ol>
      <details className="issue-details">
        <summary className="cursor-pointer font-bold">{t('results.start.how.title')}</summary>
        <p className="text-sm">{t('results.start.how.intro')}</p>
        <dl className="summary-how">
          {(['alta', 'media', 'baja', 'pendiente'] as const).map((priority) => (
            <div key={priority}>
              <dt>{t(`results.priority.${priority}`)}</dt>
              <dd>{t(`results.start.how.${priority}`)}</dd>
            </div>
          ))}
        </dl>
        <p className="text-sm">{t('results.start.how.order')}</p>
      </details>
    </section>
  );
}

/**
 * The estimate: disclaimer outside the score card, title and score inside the card (`ScoreHero`).
 * How the estimate is worked out lives in «Cómo se cuentan estas cifras» (`CountingDetails`).
 */
export function ScoreDetails({
  scan,
  /** When false, the methodology link is omitted (it already sits in `CountingDetails` beside this). */
  showMethod = true,
}: {
  scan: ScanDetail;
  showMethod?: boolean;
}) {
  const { t, i18n: i18next } = useTranslation();
  // A check of corrections analysed some pages only: it has no estimate of the website.
  if (scan.type === 'comprobacion') return null;
  return (
    <section className="score-details" aria-labelledby="estimacion-automatica">
      <p className="score-details-intro">
        {t('results.score.intro')}
        {showMethod && (
          <>
            {' '}
            <WebsiteLink href={publicPage(i18next.language, 'methodology')}>
              {t('results.score.method')}
            </WebsiteLink>
          </>
        )}
      </p>
      <div className="score-details-body">
        <ScoreHero
          scan={scan}
          comparison={scan.comparison}
          engineScore={scan.engineScores?.accesibilidad.score}
          discarded={scan.discarded}
        />
      </div>
    </section>
  );
}

/** The passed checks of the scan or of a page: applied, found nothing, still to be reviewed by a person. */
function PassedPanel({
  available,
  passed,
  scope,
}: {
  available: boolean;
  passed: PassedCheck[];
  scope: 'scan' | 'page';
}) {
  const { t } = useTranslation();
  const wcag = passed.filter((check) => check.kind === 'wcag');
  const practices = passed.filter((check) => check.kind !== 'wcag');
  // Compact: the name and the WCAG reference; the rest opens when someone wants it (once, no repeated text).
  const list = (checks: PassedCheck[]) => (
    <ul className="passed-list">
      {checks.map((check) => (
        <li key={check.ruleId}>
          <details className="passed-check">
            <summary>
              <span className="passed-name">{check.title}</span>
              {check.criteria.length > 0 && (
                <span className="passed-ref">{t('scan.criteria', { list: check.criteria.join(', ') })}</span>
              )}
              <ChevronDownIcon className="passed-arrow" aria-hidden="true" />
            </summary>
            <div className="passed-body">
              {check.description && <p>{check.description}</p>}
              {check.criteriaDetail.length > 0 && (
                <ul className="passed-criteria">
                  {check.criteriaDetail.map((criterion) => (
                    <li key={criterion.id}>
                      <strong>{criterion.id}</strong> {criterion.title}
                      {criterion.level && (
                        <span className="text-ink-muted">
                          {' '}
                          · {t('results.passed.level', { level: criterion.level })}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <p>
                <strong>{t('results.passed.result')}</strong>{' '}
                {scope === 'scan'
                  ? t('results.passed.resultPages', { count: check.pages })
                  : t('results.passed.resultPage')}
              </p>
              <p className="text-sm text-ink-muted">{t('results.passed.where')}</p>
            </div>
          </details>
        </li>
      ))}
    </ul>
  );
  return (
    <>
      <p>{t(`results.passed.intro.${scope}`)}</p>
      <Note>{t('results.passed.note')}</Note>
      {!available ? (
        <p>{t('results.passed.unavailable')}</p>
      ) : passed.length === 0 ? (
        <p>{t('results.passed.none')}</p>
      ) : (
        <>
          {wcag.length > 0 && (
            <section aria-labelledby="superadas-wcag" className="passed-group">
              <h4 id="superadas-wcag" className="text-lg font-bold">
                {t('results.passed.wcag', { count: wcag.length })}
              </h4>
              {list(wcag)}
            </section>
          )}
          {practices.length > 0 && (
            <section aria-labelledby="superadas-bp" className="passed-group">
              <h4 id="superadas-bp" className="text-lg font-bold">
                {t('results.passed.practices', { count: practices.length })}
              </h4>
              {list(practices)}
            </section>
          )}
        </>
      )}
    </>
  );
}

/** What the automated test could not decide, and the pages that were not analysed (never read as passed). */
function UncheckedPanel({
  review,
  notAnalysed,
  render,
  scope,
}: {
  review: Problem[];
  notAnalysed: ScannedPage[];
  render: (problem: Problem) => ReactNode;
  scope: 'scan' | 'page';
}) {
  const { t, i18n: i18next } = useTranslation();
  return (
    <>
      <p>{t('results.unchecked.intro')}</p>
      <section aria-labelledby="por-decidir" className="unchecked-group">
        <h4 id="por-decidir" className="text-lg font-bold">
          {t('results.unchecked.byHand', { count: review.length })}
        </h4>
        <p className="text-sm text-ink-muted">{t('results.unchecked.byHandHint')}</p>
        {review.length === 0 ? (
          <p>{t('results.unchecked.noneByHand')}</p>
        ) : (
          <ul className="rule-list">{review.map(render)}</ul>
        )}
      </section>
      {scope === 'scan' && (
        <section aria-labelledby="sin-analizar" className="unchecked-group">
          <h4 id="sin-analizar" className="text-lg font-bold">
            {t('results.unchecked.pages', { count: notAnalysed.length })}
          </h4>
          {notAnalysed.length === 0 ? (
            <p>{t('results.unchecked.noPages')}</p>
          ) : (
            <>
              <p className="text-sm text-ink-muted">{t('results.unchecked.pagesHint')}</p>
              <ul className="pending-pages">
                {notAnalysed.map((page) => (
                  <li key={page.key}>
                    <span className="wrap-anywhere font-bold">{pathOf(page.finalUrl || page.url)}</span>{' '}
                    <span className="text-ink-muted">
                      {i18next.language.startsWith('es') ? (
                        (page.blockedReason ?? page.error ?? '')
                      ) : (
                        <span lang="es">{page.blockedReason ?? page.error ?? ''}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </>
  );
}

/** The progress of the manual checks, apart from the automatic analysis and from the work on the problems. */
function ManualProgressBlock({ manual }: { manual: ManualReview }) {
  const { t } = useTranslation();
  const progress = manual.progress;
  const percent = progress.total === 0 ? 0 : Math.round((progress.registered / progress.total) * 100);
  const answers = REVIEW_ANSWERS.filter((answer) => progress.answers[answer] > 0);
  return (
    <section aria-labelledby="progreso-manual" className="manual-progress">
      <h4 id="progreso-manual" className="text-lg font-bold">
        {t('manual.progress.title')}
      </h4>
      <p className="summary-big">
        {t('manual.progress.summary', { registered: progress.registered, total: progress.total })}
      </p>
      {/* The bar is only a picture of the sentence above: its text is the label. */}
      <div
        role="img"
        aria-label={t('manual.progress.text', { registered: progress.registered, total: progress.total })}
        className="manual-bar"
      >
        <span style={{ width: `${percent}%` }} />
      </div>
      {progress.started > progress.registered && (
        <p className="text-sm">
          {t('manual.progress.started', { count: progress.started - progress.registered })}
        </p>
      )}
      {answers.length > 0 && (
        <>
          <p className="font-bold">{t('manual.progress.answers')}</p>
          <ul className="manual-list-plain">
            {answers.map((answer) => (
              <li key={answer}>
                {t(`manual.answer.${answer}`)}: <strong>{progress.answers[answer]}</strong>
              </li>
            ))}
          </ul>
        </>
      )}
      {progress.needNew > 0 && (
        <p className="text-sm">{t('manual.progress.needNew', { count: progress.needNew })}</p>
      )}
      <p className="text-sm text-ink-muted">{t('manual.progress.note')}</p>
    </section>
  );
}

/**
 * The criteria of the standard, with the automatic analysis and the manual review side by side and apart: a
 * criterion with no automatic findings is not reviewed, and a review can be started at any time.
 */
function ManualPanel({
  manual,
  problems,
  siteId,
  scanId,
  rights,
}: {
  manual: ManualReview;
  problems: Problem[];
  siteId: string;
  scanId: string;
  rights: Rights;
}) {
  const { t } = useTranslation();
  const date = useDate();
  const byPrinciple = new Map<string, ManualReview['criteria']>();
  for (const criterion of manual.criteria)
    byPrinciple.set(criterion.principle, [...(byPrinciple.get(criterion.principle) ?? []), criterion]);
  const problemsOf = (criterion: string) =>
    problems.filter((problem) => problem.criteria.includes(criterion) && problem.result === 'violation');
  return (
    <>
      <p>{t('manual.intro')}</p>
      <p>{t('manual.available')}</p>
      <Note>{t('manual.notAudit')}</Note>
      <ManualProgressBlock manual={manual} />
      <p>{t('results.manual.intro')}</p>
      {[...byPrinciple].map(([principle, criteria]) => (
        <details key={principle} className="issue-details manual-principle">
          <summary className="cursor-pointer font-bold">
            {criteria[0]?.principleName} ({criteria.length})
          </summary>
          <ul className="manual-list">
            {criteria.map((criterion) => {
              const review = criterion.review;
              const label =
                review.state === 'registrada'
                  ? 'view'
                  : review.state === 'pendiente'
                    ? 'resume'
                    : rights.track
                      ? 'start'
                      : 'guide';
              return (
                <li key={criterion.id} data-status={criterion.status} data-manual={review.state}>
                  <p>
                    <strong>
                      {criterion.id} {criterion.title}
                    </strong>{' '}
                    <span className="text-ink-muted">({criterion.level})</span>
                  </p>
                  <p className="text-sm">{criterion.summary}</p>
                  <p className="problem-chips">
                    <HelpBadge help={review.guide?.help ?? null} />
                    {review.needsNew && (
                      <span className="origin-chip manual-needs-new">{t('manual.needsNew')}</span>
                    )}
                  </p>
                  <dl className="manual-lines">
                    <div>
                      <dt>{t('manual.row.automatic')}</dt>
                      <dd>
                        {t(`results.manual.status.${criterion.status}`)}
                        {criterion.status === 'incumple' && problemsOf(criterion.id).length > 0 && (
                          <>
                            {' '}
                            ·{' '}
                            {problemsOf(criterion.id)
                              .map((problem) => problem.title)
                              .join(', ')}
                          </>
                        )}
                        {criterion.toReview > 0 && (
                          <> · {t('results.manual.toReview', { count: criterion.toReview })}</>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>{t('manual.row.manual')}</dt>
                      <dd>
                        {review.state === 'registrada' && review.answer ? (
                          <>
                            <strong>{t(`manual.answer.${review.answer}`)}</strong>
                            {review.answeredAt && (
                              <span className="text-ink-muted">
                                {' '}
                                ·{' '}
                                {t('manual.row.by', {
                                  name: review.by?.name || t('manual.row.someone'),
                                  date: date(review.answeredAt, 'short'),
                                })}
                              </span>
                            )}
                            <span className="text-ink-muted">
                              {' '}
                              · {t('manual.row.reviews', { count: review.reviews })}
                              {review.pagesReviewed > 0 && (
                                <> {t('manual.row.pages', { count: review.pagesReviewed })}</>
                              )}
                            </span>
                          </>
                        ) : (
                          <strong>{t(`manual.state.${review.state}`)}</strong>
                        )}
                        {review.drafts > 0 && (
                          <span className="text-ink-muted">
                            {' '}
                            · {t('manual.row.drafts', { count: review.drafts })}
                          </span>
                        )}
                        {review.needsNew && <span className="block text-sm">{t('manual.needsNewHint')}</span>}
                        {!review.guide && <span className="block text-sm">{t('manual.noGuide')}</span>}
                      </dd>
                    </div>
                  </dl>
                  {review.guide && (
                    <div>
                      <GuideLink siteId={siteId} criterion={criterion.id} search={{ scan: scanId }}>
                        {t(`manual.actions.${label}`)}
                        <span className="sr-only">
                          {' '}
                          — {criterion.id} {criterion.title}
                        </span>
                      </GuideLink>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      ))}
    </>
  );
}

const count = (problems: Problem[], priority: Priority) =>
  problems.filter((problem) => problem.priority === priority).length;

/**
 * The findings in categories that are never mixed: problems, checks without findings, what could not be
 * checked, the manual review (only for a whole scan) and best practices, SEO and quality, which are not
 * accessibility problems.
 */
export function ResultsTabs({
  scope,
  issues,
  checks,
  manual,
  pages,
  scanId,
  siteId,
  rights,
  tab,
  onTab,
  reveal,
  pageContext,
  persistKey,
  scan,
}: {
  /** The analysis (in a whole analysis): an opened problem shows the same solution as in a page. */
  scan?: ScanDetail;
  scope: 'scan' | 'page';
  issues: (ScanIssue | PageIssue)[];
  checks: { available: boolean; passed: PassedCheck[] };
  manual: ManualReview | null;
  pages: ScannedPage[];
  scanId: string;
  siteId: string;
  rights: Rights;
  tab: TabKey;
  onTab: (tab: TabKey) => void;
  /** A problem to open and bring into view (from "where to begin"). */
  reveal: { key: string; nonce: number } | null;
  /** On a page: opens the problem's elements on the capture, one by one. */
  pageContext?: PageContext;
  /** What the open problems and the filters are kept under, so coming back finds them as they were. */
  persistKey: string;
}) {
  const { t } = useTranslation();
  const split = useMemo(() => splitCategories(issues), [issues]);
  const grouped = useMemo(() => groupProblems(split.problems), [split.problems]);
  // A problem whose elements are all discarded with a justification is not among the problems to fix: it
  // is listed apart, with its justification, and does not count in the totals.
  // On a page, what is there only through shared components that another page looks after is not its own problem:
  // it is not listed or counted here, and a note points to the page that looks after it.
  const problems = useMemo(
    () => grouped.filter((problem) => !isFullyDiscarded(problem) && !isDelegatedOnly(problem)),
    [grouped],
  );
  const delegatedProblems = useMemo(() => grouped.filter(isDelegatedOnly), [grouped]);
  const discardedProblems = useMemo(() => grouped.filter(isFullyDiscarded), [grouped]);
  const review = useMemo(() => groupProblems(split.unchecked), [split.unchecked]);
  const other = useMemo(
    () => ({
      bestPractices: groupProblems(split.bestPractices),
      seo: groupProblems(split.seo),
      quality: groupProblems(split.quality),
    }),
    [split.bestPractices, split.seo, split.quality],
  );
  const otherAll = useMemo(() => [...other.bestPractices, ...other.seo, ...other.quality], [other]);
  const notAnalysed = pages.filter((page) => page.blocked || page.error);
  const [filters, setFilters] = usePersisted<Record<'problems' | 'other', Filters>>(`filters:${persistKey}`, {
    problems: NO_FILTERS,
    other: NO_FILTERS,
  });
  const [openKeys, setOpenKeys] = usePersisted<string[]>(`open:${persistKey}`, []);
  // On a page, what is not a problem to fix lives in a folded section.
  const [moreOpen, setMoreOpen] = useState(false);
  // Going back from a problem's panel returns to its card in the list.
  const openKey = pageContext?.problemKey;
  const lastOpen = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (lastOpen.current && !openKey) {
      const id = anchorOf(lastOpen.current);
      requestAnimationFrame(() => document.getElementById(id)?.querySelector('button')?.focus());
    }
    lastOpen.current = openKey;
  }, [openKey]);
  const open = useMemo(() => new Set(openKeys), [openKeys]);
  const everyProblem = useMemo(
    () =>
      new Map(
        [...problems, ...delegatedProblems, ...discardedProblems, ...review, ...otherAll].map((problem) => [
          problem.key,
          problem,
        ]),
      ),
    [problems, delegatedProblems, discardedProblems, review, otherAll],
  );
  const toggle = (key: string) => {
    const opening = !open.has(key);
    setOpenKeys((before) => (opening ? [...before, key] : before.filter((candidate) => candidate !== key)));
    // On a page, opening a problem opens its first element on the capture; closing it lets go of that element.
    const problem = everyProblem.get(key);
    if (scope === 'page' && pageContext && problem) {
      const elements = elementsOf(problem as Problem<PageIssue>);
      if (opening) {
        if (elements[0]) pageContext.onSelect(elements[0].id);
      } else if (elements.some((element) => element.occurrenceIds.includes(pageContext.selectedId ?? ''))) {
        pageContext.onSelect(null);
      }
    }
  };

  // An element opened from elsewhere (the capture, an address): its problem opens, in its own tab, in view.
  const selectedId = pageContext?.selectedId;
  useEffect(() => {
    // On a page the panel of the problem takes the place of the list: the page decides which one is open.
    if (!selectedId || scope === 'page') return;
    for (const problem of everyProblem.values()) {
      if (
        !elementsOf(problem as Problem<PageIssue>).some((element) =>
          element.occurrenceIds.includes(selectedId),
        )
      )
        continue;
      onTab(tabOf(problem));
      setOpenKeys((before) => (before.includes(problem.key) ? before : [...before, problem.key]));
      requestAnimationFrame(() =>
        document.getElementById(anchorOf(problem.key))?.scrollIntoView({ block: 'nearest' }),
      );
      return;
    }
  }, [selectedId, scope, everyProblem, onTab, setOpenKeys]);

  // From "where to begin": the problem opens and the view goes to it.
  const lastReveal = useRef(0);
  useEffect(() => {
    if (!reveal || reveal.nonce === lastReveal.current || tab !== 'problems') return;
    lastReveal.current = reveal.nonce;
    // The problem is shown whatever the filters say, and opened where it is in the list.
    setFilters((before) => (hasFilters(before.problems) ? { ...before, problems: NO_FILTERS } : before));
    setOpenKeys((before) => (before.includes(reveal.key) ? before : [...before, reveal.key]));
    requestAnimationFrame(() => {
      const card = document.getElementById(anchorOf(reveal.key));
      card?.scrollIntoView({ block: 'start' });
      card?.querySelector<HTMLElement>('button')?.focus();
    });
  }, [reveal, tab, setOpenKeys, setFilters]);

  const render = (problem: Problem) => (
    <ProblemCard
      key={problem.key}
      problem={problem}
      scope={scope}
      open={open.has(problem.key)}
      onToggle={() => toggle(problem.key)}
      rights={rights}
      siteId={siteId}
      scanId={scanId}
      pages={pages}
      scan={scan}
      anchor={anchorOf(problem.key)}
      pageContext={pageContext}
    />
  );

  const shownProblems = applyFilters(problems, filters.problems);
  const shownOther = {
    bestPractices: applyFilters(other.bestPractices, filters.other),
    seo: applyFilters(other.seo, filters.other),
    quality: applyFilters(other.quality, filters.other),
  };
  const shownOtherCount = shownOther.bestPractices.length + shownOther.seo.length + shownOther.quality.length;

  const tabs: { key: TabKey; label: string; count: number | null }[] = [
    ...(scope === 'scan'
      ? [{ key: 'problems' as const, label: t('results.tabs.problems'), count: problems.length }]
      : []),
    { key: 'passed', label: t('results.tabs.passed'), count: checks.available ? checks.passed.length : null },
    {
      key: 'unchecked',
      label: t('results.tabs.unchecked'),
      count: review.length + (scope === 'scan' ? notAnalysed.length : 0),
    },
    ...(scope === 'scan' && manual
      ? [{ key: 'manual' as const, label: t('results.tabs.manual'), count: manual.total - manual.registered }]
      : []),
    { key: 'other', label: t('results.tabs.other'), count: otherAll.length },
  ];

  // The problems come straight under their title: no explanation, no other list before them.
  const problemsSection = (
    <section aria-labelledby="problemas-detectados" className="problems-section">
      <h3 id="problemas-detectados" className="text-xl font-bold">
        {t('results.tabs.problems')}
      </h3>
      {problems.length > 0 && (
        <FilterBar
          inline={scope === 'scan'}
          filters={filters.problems}
          onChange={(next) => setFilters({ ...filters, problems: next })}
          shown={shownProblems.length}
          total={problems.length}
          counts={{
            alta: count(problems, 'alta'),
            media: count(problems, 'media'),
            baja: count(problems, 'baja'),
          }}
        />
      )}
      {problems.length === 0 ? (
        <p>{t(`results.problems.none.${scope}`)}</p>
      ) : shownProblems.length === 0 ? (
        <p>{t('results.filters.noMatch')}</p>
      ) : (
        <ul className="rule-list">{shownProblems.map(render)}</ul>
      )}
      {scope === 'page' && delegatedProblems.length > 0 && pageContext && (
        <section aria-labelledby="globales-aqui" className="problem-delegated">
          <h4 id="globales-aqui" className="text-lg font-bold">
            {t('results.globals.title')}
          </h4>
          <p className="text-sm text-ink-muted">
            {t('results.shared.alsoHere', { count: delegatedProblems.length })}
          </p>
          <ul className="rule-list">
            {delegatedProblems.map((problem) => {
              const component = problem.shared.find((candidate) => candidate.ownerKey) ?? problem.shared[0];
              const owner = component?.ownerKey
                ? pageContext.scan.pages.find((candidate) => candidate.key === component.ownerKey)
                : undefined;
              return (
                <li key={problem.key} className="rule-card problem-card">
                  <div className="problem-head">
                    <p className="problem-title">{problem.title}</p>
                    <div className="problem-meta">
                      <PriorityChip priority={problem.priority} />
                      {owner ? (
                        <Link
                          to="/sites/$siteId/paginas"
                          params={{ siteId }}
                          search={{ scan: scanId, p: owner.key, k: problem.key }}
                          className="wrap-anywhere"
                        >
                          {t('results.shared.owner', { page: owner.title || pathOf(owner.finalUrl) })}
                        </Link>
                      ) : (
                        <span>{t('results.shared.ownerGlobal')}</span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {discardedProblems.length > 0 && (
        <section aria-labelledby="descartados" className="follow-discarded-problems">
          <h4 id="descartados" className="text-lg font-bold">
            {t('follow.discardedList.title')}
          </h4>
          <p className="text-sm text-ink-muted">{t('follow.discardedList.intro')}</p>
          <ul className="rule-list">{discardedProblems.map(render)}</ul>
        </section>
      )}
    </section>
  );

  // What each choice shows. In the tabs of an analysis each is a panel; on a page they are the options of one selector.
  const bodies = {
    problems: problemsSection,
    passed: (
      <>
        <h3 className="text-xl font-bold">{t('results.tabs.passed')}</h3>
        <PassedPanel available={checks.available} passed={checks.passed} scope={scope} />
      </>
    ),
    unchecked: (
      <>
        <h3 className="text-xl font-bold">{t('results.tabs.unchecked')}</h3>
        <UncheckedPanel review={review} notAnalysed={notAnalysed} render={render} scope={scope} />
      </>
    ),
    manual:
      scope === 'scan' && manual ? (
        <>
          <h3 className="text-xl font-bold">{t('results.tabs.manual')}</h3>
          <ManualPanel manual={manual} problems={problems} siteId={siteId} scanId={scanId} rights={rights} />
        </>
      ) : null,
    other: (
      <>
        <h3 className="text-xl font-bold">{t('results.tabs.other')}</h3>
        <p>{t('results.other.intro')}</p>
        <Note>{t('results.other.note')}</Note>
        {otherAll.length > 0 && (
          <FilterBar
            inline={scope === 'scan'}
            areas
            filters={filters.other}
            onChange={(next) => setFilters({ ...filters, other: next })}
            shown={shownOtherCount}
            total={otherAll.length}
            counts={{
              alta: count(otherAll, 'alta'),
              media: count(otherAll, 'media'),
              baja: count(otherAll, 'baja'),
            }}
          />
        )}
        {(['bestPractices', 'seo', 'quality'] as const).map((group) => (
          <section key={group} aria-labelledby={`otras-${group}`} className="other-group">
            <h4 id={`otras-${group}`} className="text-lg font-bold">
              {t(`results.other.${group}`)} ({shownOther[group].length})
            </h4>
            <p className="text-sm text-ink-muted">{t(`results.other.${group}Hint`)}</p>
            {shownOther[group].length === 0 ? (
              <p>{t(other[group].length === 0 ? 'results.other.empty' : 'results.filters.noMatch')}</p>
            ) : (
              <ul className="rule-list">{shownOther[group].map(render)}</ul>
            )}
          </section>
        ))}
      </>
    ),
  };

  const openProblem = openKey ? everyProblem.get(openKey) : undefined;
  if (scope === 'page' && pageContext && openProblem) {
    const pageProblem = openProblem as Problem<PageIssue>;
    return (
      <CorrectionPanel
        // Another problem starts the panel from scratch, and takes the focus to its name.
        key={pageProblem.key}
        problem={pageProblem}
        elements={elementsOf(pageProblem)}
        selectedId={pageContext.selectedId}
        onSelect={pageContext.onSelect}
        onBack={() => pageContext.onOpenProblem(null)}
        device={pageContext.device}
        scan={pageContext.scan}
        page={pageContext.page}
        siteId={siteId}
        rights={rights}
        imageHeight={pageContext.heights[pageContext.device] ?? null}
        stacked={pageContext.stacked}
        onShowCapture={pageContext.onShowCapture}
      />
    );
  }
  // One selector instead of a menu of tabs: the choices say how many there are in each, only the one chosen shows, and
  // a native select works with a keyboard, a screen reader and a phone alike. On a page the problems come first and the
  // rest is a folded section; in a whole analysis the selector chooses among every category, «Problemas detectados» first.
  const selector = (choices: typeof tabs, chosen: TabKey) => (
    <>
      <label className="more-select" htmlFor={`categoria-${scope}`}>
        <span>{t('results.more.show')}</span>
        <select
          id={`categoria-${scope}`}
          value={chosen}
          onChange={(event) => onTab(event.target.value as TabKey)}
          className="min-h-11 w-full rounded-md border border-field-border bg-paper px-3 py-2 text-ink"
        >
          {choices.map((item) => (
            <option key={item.key} value={item.key}>
              {item.count === null ? item.label : `${item.label} (${item.count})`}
            </option>
          ))}
        </select>
      </label>
      <div className="results-panel">{bodies[chosen as keyof typeof bodies]}</div>
    </>
  );
  if (scope === 'page') {
    const choices = tabs.filter(
      (item) => item.key === 'passed' || item.key === 'unchecked' || item.key === 'other',
    );
    const chosen = (choices.find((item) => item.key === tab) ?? choices[0]!).key;
    return (
      <div className="page-results">
        {problemsSection}
        <details
          className="more-results"
          open={moreOpen}
          onToggle={(event) => setMoreOpen(event.currentTarget.open)}
        >
          <summary>
            <span>{t('results.more.title')}</span>
            <ChevronDownIcon className="page-tech-arrow" aria-hidden="true" />
          </summary>
          {selector(choices, chosen)}
        </details>
      </div>
    );
  }

  const chosen = (tabs.find((item) => item.key === tab) ?? tabs[0]!).key;
  return <div className="results-select">{selector(tabs, chosen)}</div>;
}

/**
 * The whole results of a scan: follow-up and manual summaries, where to begin, the score as secondary
 * information and the categories. The analysis figures are on the Análisis tab.
 */
export function ResultsView({
  scan,
  issues,
  siteId,
  rights,
}: {
  scan: ScanDetail;
  issues: ScanIssue[];
  siteId: string;
  rights: Rights;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = usePersisted<TabKey>(`tab:${scan.id}`, 'problems');
  const [reveal, setReveal] = useState<{ key: string; nonce: number } | null>(null);
  const split = useMemo(() => splitCategories(issues), [issues]);
  const problems = useMemo(
    () => groupProblems(split.problems).filter((problem) => !isFullyDiscarded(problem)),
    [split.problems],
  );
  const review = useMemo(() => groupProblems(split.unchecked), [split.unchecked]);
  const checks = scan.checks ?? { available: false, analysedPages: 0, passed: [] };
  return (
    <div className="results">
      {scan.type === 'comprobacion' && <CheckOutcome scan={scan} siteId={siteId} headingLevel={3} />}
      <section aria-labelledby="resumenes" className="results-summaries">
        <h2 id="resumenes" tabIndex={-1} className="sr-only focus:outline-none">
          {t('results.summaries')}
        </h2>
        {/* The analysis figures stay on the Análisis tab; here only follow-up and manual review. */}
        <FollowSummaryCard siteId={siteId} />
        <ManualSummary
          manual={scan.manual}
          review={review}
          onOpen={() => {
            setTab('manual');
            requestAnimationFrame(() =>
              document.getElementById('detalle-resultados')?.scrollIntoView({ block: 'start' }),
            );
          }}
        />
      </section>
      <StartHere
        problems={problems}
        onOpen={(problem) => {
          setTab('problems');
          setReveal({ key: problem.key, nonce: Date.now() });
        }}
      />
      <ScoreDetails scan={scan} />
      <Honesty />
      <section aria-labelledby="detalle-resultados" className="results-detail">
        <h2 id="detalle-resultados" className="issues-title">
          {t('results.detail.title')}
        </h2>
        <ResultsTabs
          scope="scan"
          issues={issues}
          checks={checks}
          manual={scan.manual}
          pages={scan.pages}
          scanId={scan.id}
          siteId={siteId}
          rights={rights}
          tab={tab}
          onTab={setTab}
          reveal={reveal}
          persistKey={scan.id}
          scan={scan}
        />
      </section>
    </div>
  );
}

/** A notice when what is on screen is not the latest result of the website. */
export function PreviousNotice({
  latest,
  siteId,
}: {
  latest: { id: string; createdAt: string };
  siteId: string;
}) {
  const { t } = useTranslation();
  const date = useDate();
  return (
    <p className="results-note results-previous" role="note">
      <span>
        <strong>{t('results.previous.title')}</strong>{' '}
        {t('results.previous.text', { date: date(latest.createdAt) })}{' '}
        <Link to="/sites/$siteId/scans/$scanId" params={{ siteId, scanId: latest.id }}>
          {t('results.previous.link')}
        </Link>
      </span>
    </p>
  );
}

/** What the results on screen are: which analysis, when, on which devices, whole or partial, latest or earlier. */
export function ResultContext({ scan, siteId }: { scan: ScanDetail; siteId: string }) {
  const { t } = useTranslation();
  const date = useDate();
  const latest = useQuery(scansQuery(siteId)).data?.[0];
  const previous = latest !== undefined && latest.id !== scan.id;
  return (
    <ul className="result-context" aria-label={t('results.context.label')}>
      <li>{t('results.context.date', { date: date(scan.createdAt) })}</li>
      <li>{t('results.context.devices')}</li>
      <li>
        {scan.outcome === 'partial'
          ? t('results.context.partial', { count: scan.pagesNotAnalysed ?? 0 })
          : t('results.context.complete')}
      </li>
      {previous && <li>{t('results.context.previous')}</li>}
    </ul>
  );
}

/**
 * The summary of one page, in one compact line and in the same units as the scan's: problems (one per
 * rule) and the distinct elements behind them, with the priorities small beside it.
 */
export function PageSummary({
  problems,
  page,
  delegatedElements = 0,
}: {
  problems: Problem[];
  page: ScannedPage;
  /** Elements of shared components that another page looks after: they are not this page's own. */
  delegatedElements?: number;
}) {
  const { t } = useTranslation();
  const own = page.counts
    ? { ...page, counts: { ...page.counts, nodes: Math.max(0, page.counts.nodes - delegatedElements) } }
    : page;
  const summary = summarize(problems, [own]);
  return (
    <section className="page-summary" aria-label={t('results.pageSummary.title')}>
      {summary.problems === 0 ? (
        <p>{t('results.pageSummary.none')}</p>
      ) : (
        <>
          <p className="page-summary-line">
            <strong>{t('results.pageSummary.problems', { count: summary.problems })}</strong>
            {summary.global > 0 && (
              <span className="text-ink-muted">
                {' '}
                ({t('results.pageSummary.global', { count: summary.global })})
              </span>
            )}
            <span aria-hidden="true"> · </span>
            <strong>
              {t('results.pageSummary.elements', {
                count: summary.elements,
                plus: summary.capped ? '+' : '',
              })}
            </strong>
          </p>
          <p className="summary-priorities">
            {(['alta', 'media', 'baja'] as const)
              .filter((priority) => summary.byPriority[priority] > 0)
              .map((priority) => (
                <span key={priority} className="tone-chip" data-tone={PRIORITY_TONE[priority]}>
                  <ToneIcon tone={PRIORITY_TONE[priority]} />
                  {t(`results.pageSummary.priorities.${priority}`, { count: summary.byPriority[priority] })}
                </span>
              ))}
          </p>
        </>
      )}
    </section>
  );
}
